import React from 'react';
import { SelectOption } from 'global';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Typography, Button } from '@mui/material';
import { LenderProfiles } from '@trnsact/trnsact-shared-types/src/generated';
import { AutocompleteField, InputField, QuillInputField, SwitchField } from 'components/form';
import { FinanceProgramForm } from '../../../../schema';
import { LendersAutocompleteField } from './LendersAutocompleteField';

interface Props {
  isReadOnly: boolean;
  isMultipleLenders?: boolean;
  userType?: 'lender' | 'dealer';
  lendersOptions: LenderProfiles[];
  isLendersSelectDisabled?: boolean;
  dealerVendorProfileOptions?: any[];
  isLenderCreditAppSettingVisible?: boolean;
}

export const ProgramDetails = ({
  userType,
  isReadOnly,
  lendersOptions,
  isMultipleLenders,
  isLendersSelectDisabled,
  dealerVendorProfileOptions = [],
  isLenderCreditAppSettingVisible = false,
}: Props) => {
  const { control, setValue, getValues } = useFormContext<FinanceProgramForm>();

  const dealerOptions = (dealerVendorProfileOptions ?? []).map(profile => ({
    value: profile.lenderProfiles[0]?.lenderProfileId,
    label: profile.name.replace('VP - ', ''),
    ...profile,
  }));

  const handleAddAllDealers = () => {
    setValue('program.first.dealers', dealerOptions);
  };

  const handleAddAllActiveDealers = () => {
    const activeDealers = dealerOptions.filter(dealer => dealer?.vpStatus === 'active');
    setValue('program.first.dealers', activeDealers);
  };

  return (
    <>
      <Box className="formRow">
        <InputField
          control={control}
          name="program.first.name"
          label="Finance Program Name"
          inputProps={{ required: true, disabled: isReadOnly, autoComplete: 'off', placeholder: 'Enter name' }}
        />

        <InputField
          control={control}
          name="program.first.displayName"
          label="Finance Program Display Name"
          tooltip="This name will be shown to customers"
          inputProps={{ required: true, autoComplete: 'off', disabled: isReadOnly, placeholder: 'Enter display name' }}
        />
      </Box>

      {!!lendersOptions.length && (
        <Box className="formSection">
          <LendersAutocompleteField
            lendersOptions={lendersOptions}
            isMultipleLenders={isMultipleLenders}
            isLendersSelectDisabled={isLendersSelectDisabled}
          />

          {isLenderCreditAppSettingVisible && (
            <SwitchField
              control={control}
              disabled={isReadOnly}
              label="Link to Lender Credit App"
              name="program.first.linkToLenderCreditApp"
            />
          )}
        </Box>
      )}

      {userType === 'lender' && !!dealerVendorProfileOptions?.length && (
        <Box sx={{ display: 'grid', alignItems: 'center', gridTemplateColumns: '1fr auto auto', gap: '1rem' }}>
          <AutocompleteField<SelectOption, true, FinanceProgramForm>
            multiple
            control={control}
            disableCloseOnSelect
            name="program.first.dealers"
            options={dealerOptions}
            disabled={!dealerVendorProfileOptions?.length}
            isOptionEqualToValue={(option, value) => value && option.value === value?.value}
            textFieldProps={{
              label: 'Dealers this program is available to',
              placeholder: 'Search by name...',
            }}
          />

          <Button variant="contained" onClick={handleAddAllDealers}>
            Add all
          </Button>

          <Button variant="contained" onClick={handleAddAllActiveDealers}>
            Add all active
          </Button>
        </Box>
      )}

      <Box className="formSectionWithCustomLabel">
        <Typography component="span" variant="body1">
          Program Guidelines
        </Typography>

        <QuillInputField
          control={control}
          setValue={setValue}
          readOnly={isReadOnly}
          getValues={getValues}
          name="program.first.guidelines"
        />
      </Box>

      <Box className="formSectionWithCustomLabel">
        <Typography component="span" variant="body1">
          Internal Notes
        </Typography>

        <InputField
          control={control}
          name="program.first.notes"
          inputProps={{ variant: 'outlined', disabled: isReadOnly, placeholder: 'Enter your notes' }}
        />
      </Box>
    </>
  );
};
